import type { CrmData, Stage } from "../types";
import { computeWarnings, isOverdue } from "./businessRules";
import { computeInvoiceTotals, isInvoiceOverdue } from "./billing";

const OPEN_STAGES: Stage[] = ["New Lead", "Qualified", "Proposal Sent", "Negotiation"];

export interface Kpis {
  openPipeline: number;
  weightedForecast: number;
  wonRevenue: number;
  wonCount: number;
  lostCount: number;
  winRate: number; // 0-100
  avgDealSize: number;
  activeCustomers: number;
}

export function computeKpis(data: CrmData): Kpis {
  const open = data.opportunities.filter((o) => o.stage !== "Won" && o.stage !== "Lost");
  const won = data.opportunities.filter((o) => o.stage === "Won");
  const lost = data.opportunities.filter((o) => o.stage === "Lost");
  const wonRevenue = won.reduce((s, o) => s + (o.finalValue ?? o.value ?? 0), 0);
  const closed = won.length + lost.length;
  return {
    openPipeline: open.reduce((s, o) => s + (o.value ?? 0), 0),
    weightedForecast: Math.round(open.reduce((s, o) => s + ((o.value ?? 0) * o.probability) / 100, 0)),
    wonRevenue,
    wonCount: won.length,
    lostCount: lost.length,
    winRate: closed === 0 ? 0 : Math.round((won.length / closed) * 100),
    avgDealSize: won.length === 0 ? 0 : Math.round(wonRevenue / won.length),
    activeCustomers: data.companies.filter((c) => c.status === "Active Customer").length,
  };
}

export interface StageDatum {
  stage: Stage;
  count: number;
  value: number;
}

export function pipelineByStage(data: CrmData): StageDatum[] {
  return OPEN_STAGES.map((stage) => {
    const opps = data.opportunities.filter((o) => o.stage === stage);
    return { stage, count: opps.length, value: opps.reduce((s, o) => s + (o.value ?? 0), 0) };
  });
}

export interface OwnerDatum {
  owner: string;
  wonValue: number;
  openValue: number;
  wonCount: number;
  openCount: number;
}

export function ownerLeaderboard(data: CrmData): OwnerDatum[] {
  const map = new Map<string, OwnerDatum>();
  for (const o of data.opportunities) {
    const row = map.get(o.salesOwner) ?? { owner: o.salesOwner, wonValue: 0, openValue: 0, wonCount: 0, openCount: 0 };
    if (o.stage === "Won") {
      row.wonValue += o.finalValue ?? o.value ?? 0;
      row.wonCount += 1;
    } else if (o.stage !== "Lost") {
      row.openValue += o.value ?? 0;
      row.openCount += 1;
    }
    map.set(o.salesOwner, row);
  }
  return Array.from(map.values()).sort((a, b) => b.wonValue - a.wonValue);
}

export interface ArSummary {
  invoiced: number;
  collected: number;
  outstanding: number;
  overdue: number;
}

export function arSummary(data: CrmData, now: Date): ArSummary {
  let invoiced = 0;
  let collected = 0;
  let outstanding = 0;
  let overdue = 0;
  for (const inv of data.invoices) {
    if (inv.status === "Void") continue;
    const { total, balance } = computeInvoiceTotals(inv);
    invoiced += total;
    collected += inv.amountPaid;
    if (inv.status === "Sent" || inv.status === "Partially Paid") outstanding += balance;
    if (isInvoiceOverdue(inv, now)) overdue += balance;
  }
  return { invoiced, collected, outstanding, overdue };
}

export interface CompanyStatusDatum {
  status: string;
  count: number;
}

export function companiesByStatus(data: CrmData): CompanyStatusDatum[] {
  const order = ["Prospect", "Active Customer", "At Risk", "Churned"];
  return order.map((status) => ({ status, count: data.companies.filter((c) => c.status === status).length }));
}

export type AttentionKind = "opportunity" | "invoice";

export interface AttentionItem {
  id: string;
  kind: AttentionKind;
  title: string;
  reason: string;
  severity: "danger" | "warning";
  amount: number;
}

/** Deal-uri si facturi care cer atentie — leaga pipeline-ul de billing intr-o singura lista actionabila. */
export function attentionItems(data: CrmData, now: Date): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const o of data.opportunities) {
    if (o.stage === "Won" || o.stage === "Lost") continue;
    const warnings = computeWarnings(o, data.activities, data.proposals, data.contacts, now);
    if (isOverdue(o, now)) {
      items.push({ id: o.id, kind: "opportunity", title: o.name, reason: "Overdue — expected close date passed", severity: "danger", amount: o.value ?? 0 });
      continue;
    }
    const follow = warnings.find((w) => w.code === "needs-follow-up");
    if (follow) {
      items.push({ id: o.id, kind: "opportunity", title: o.name, reason: follow.label, severity: "warning", amount: o.value ?? 0 });
      continue;
    }
    const expired = warnings.find((w) => w.code === "expired-proposal");
    if (expired) {
      items.push({ id: o.id, kind: "opportunity", title: o.name, reason: "Proposal expired", severity: "warning", amount: o.value ?? 0 });
    }
  }

  for (const inv of data.invoices) {
    if (isInvoiceOverdue(inv, now)) {
      const { balance } = computeInvoiceTotals(inv);
      const company = data.companies.find((c) => c.id === inv.companyId);
      items.push({ id: inv.id, kind: "invoice", title: `${inv.invoiceNumber} · ${company?.name ?? ""}`, reason: "Invoice overdue", severity: "danger", amount: balance });
    }
  }

  return items.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === "danger" ? -1 : 1;
    return b.amount - a.amount;
  });
}
